import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { MessageSquare, ShoppingBag, BookOpen, Sparkles, Search, Menu, X, ChevronRight, Star, Package, Users, Award, Sprout, Wrench, TreePine, Home as HomeIcon, Flower2, Palette, RefreshCw, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import SEO from '@/components/SEO';
import SchemaMarkup from '@/components/SchemaMarkup';
import GardenHub from './pages/GardenHub';
import ListYourBusiness from './pages/ListYourBusiness';
import AffiliateDisclosure from './pages/AffiliateDisclosure';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Contact from './pages/Contact';
import WriteForUs from './pages/WriteForUs';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [shopMenuOpen, setShopMenuOpen] = useState(false);
  const [articlesMenuOpen, setArticlesMenuOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('darkMode') === 'true';
    }
    return false;
  });
  
  // AI Chat state
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: "Hello! I'm your AI garden expert. Ask me anything about plants, gardening tips, or greenhouse care. You have 1 free question to get started!" }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [freeSearchUsed, setFreeSearchUsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('freeSearchUsed') === 'true';
    }
    return false;
  });
  const [sessionId] = useState(() => {
    if (typeof window !== 'undefined') {
      let id = localStorage.getItem('chatSessionId');
      if (!id) {
        id = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('chatSessionId', id);
      }
      return id;
    }
    return 'session_' + Date.now();
  });

  // Apply dark mode to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
  }, [darkMode]);

  // Article categories with icons and links
  const articleCategories = [
    { name: 'Growing Tips', icon: Sprout, color: 'text-green-600', count: 0 },
    { name: 'Pest Control', icon: Wrench, color: 'text-red-600', count: 0 },
    { name: 'General Gardening', icon: Flower2, color: 'text-pink-600', count: 0 },
    { name: 'Greenhouses', icon: HomeIcon, color: 'text-teal-600', count: 0 },
    { name: 'Indoor Gardening', icon: TreePine, color: 'text-blue-600', count: 0 },
    { name: 'Landscaping', icon: Palette, color: 'text-purple-600', count: 0 },
    { name: 'Seeds', icon: Sprout, color: 'text-amber-600', count: 0 },
  ];

  // Get category counts from articles
  const getCategoryCounts = () => {
    const counts = {};
    articles.forEach(a => {
      counts[a.category] = (counts[a.category] || 0) + 1;
    });
    return counts;
  };

  const categoryCounts = getCategoryCounts();

  // Fetch articles from API
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/articles?limit=100`);
        if (response.ok) {
          const data = await response.json();
          setArticles(data);
        }
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  // Sync new articles from RSS feeds
  const handleSyncArticles = async () => {
    setSyncing(true);
    try {
      const syncResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sync-rss?limit=30`, {
        method: 'POST'
      });
      if (syncResponse.ok) {
        // Refresh articles list after sync
        const articlesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/articles?limit=100`);
        if (articlesResponse.ok) {
          const data = await articlesResponse.json();
          setArticles(data);
        }
      }
    } catch (error) {
      console.error('Failed to sync articles:', error);
    } finally {
      setSyncing(false);
    }
  };

  // Send message to AI chat
  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    // Check if free search is already used
    if (freeSearchUsed) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: "You've used your free question! To continue chatting with our AI garden expert, please contact us for premium access or check back later."
      }]);
      return;
    }
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
        // Mark free search as used
        setFreeSearchUsed(true);
        localStorage.setItem('freeSearchUsed', 'true');
      } else {
        setChatMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "Sorry, I couldn't process your question. Please try again later." 
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Connection error. Please check your internet and try again." 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Helper to get articles by category
  const getArticlesByCategory = (category, limit = 4) => {
    return articles.filter(a => a.category === category).slice(0, limit);
  };

  // Get featured articles for carousel - show 10 most recent articles from various categories
  const getFeaturedArticles = () => {
    if (articles.length === 0) return [];
    
    // Get 2 articles from each major category for variety
    const featured = [];
    const categories = ['Pest Control', 'Growing Tips', 'Greenhouses', 'General Gardening', 'Landscaping', 'Indoor Gardening', 'Seeds'];
    
    categories.forEach(cat => {
      const catArticles = articles.filter(a => a.category === cat);
      // Take up to 2 from each category
      catArticles.slice(0, 2).forEach(article => featured.push(article));
    });
    
    // Shuffle and return 10 articles for the carousel
    const shuffled = featured.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 10);
  };

  // Get image for article - use image_url directly or fallback
  const getArticleImage = (article) => {
    if (article.image_url) {
      return article.image_url;
    }
    // Fallback image
    return 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&q=80';
  };

  const featuredArticles = getFeaturedArticles();

  const shopCategories = [
    { name: 'Seeds & Starts', icon: Sprout, link: '/garden-hub#seeds', color: 'text-green-600' },
    { name: 'Tools & Equipment', icon: Wrench, link: '/garden-hub#tools', color: 'text-orange-600' },
    { name: 'Plants & Trees', icon: TreePine, link: '/garden-hub#plants', color: 'text-teal-600' },
    { name: 'Indoor & Hydroponic', icon: HomeIcon, link: '/garden-hub#indoor', color: 'text-blue-600' },
    { name: 'Succulents & Gifts', icon: Flower2, link: '/garden-hub#succulents', color: 'text-pink-600' },
    { name: 'Garden Decor', icon: Palette, link: '/garden-hub#decor', color: 'text-purple-600' }
  ];

  // Auto-advance carousel
  useEffect(() => {
    const slideCount = featuredArticles.length || 1;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideCount);
    }, 5000);
    return () => clearInterval(timer);
  }, [featuredArticles.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % (featuredArticles.length || 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + (featuredArticles.length || 1)) % (featuredArticles.length || 1));
  };

  return (
    <>
      <SEO
        title="Garden & Greenhouse - Expert Gardening Tips & Products Since 1998"
        description="Your trusted source for gardening advice, premium products, AI-powered plant care, and affiliate recommendations. Discover expert tips from our comprehensive growing guides."
        keywords="gardening tips, garden advice, greenhouse, plant care, organic gardening, seeds, garden tools, landscaping, pest control, indoor gardening, AI garden assistant"
        url="https://www.gardenandgreenhouse.com"
      />
      <SchemaMarkup type="organization" />
      <SchemaMarkup type="website" />
      
      <div className="min-h-screen bg-stone-50 dark:bg-stone-900">
      {/* Sticky Header Container */}
      <div className="sticky top-0 z-50">
        {/* Top Banner */}
        <div className="bg-emerald-700 text-white text-center py-2 px-4 text-sm font-medium hidden sm:block">
          ✨ Free Shipping on Orders Over $50 | Expert Gardening Advice & Premium Products
        </div>

        {/* Navigation */}
        <nav className="bg-white/95 dark:bg-stone-900/95 backdrop-blur-md border-b border-stone-200 dark:border-stone-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="/" className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_eco-gardens-2/artifacts/8awup57n_GardenAndGreenhouse%20FINAL%20LOGO.png" 
                alt="Garden & Greenhouse Logo - Est. 2018" 
                className="h-16 w-auto object-contain"
              />
              <div className="flex flex-col">
                <h1 className="text-3xl font-bold text-stone-900 leading-tight tracking-tight">
                  Garden & Greenhouse
                </h1>
                <p className="text-sm text-emerald-700 font-semibold tracking-wide mt-0.5">
                  SIMPLE, PRACTICAL GARDENING TIPS
                </p>
              </div>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {/* Articles Dropdown - FIRST */}
              <div 
                className="relative"
                onMouseEnter={() => setArticlesMenuOpen(true)}
                onMouseLeave={() => setArticlesMenuOpen(false)}
              >
                <button className="nav-link flex items-center" data-testid="articles-dropdown-btn">
                  Articles
                  <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${articlesMenuOpen ? 'rotate-90' : ''}`} />
                </button>
                
                {/* Articles Mega Menu Dropdown */}
                {articlesMenuOpen && (
                  <div className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-stone-800 rounded-xl shadow-2xl border border-stone-200 dark:border-stone-700 z-50">
                    <div className="p-4">
                      <div className="mb-4 pb-4 border-b border-stone-200 dark:border-stone-700">
                        <a href="#articles" className="block">
                          <Button className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="view-all-articles-btn">
                            <BookOpen className="w-4 h-4 mr-2" />
                            View All Articles ({articles.length})
                          </Button>
                        </a>
                      </div>
                      <p className="text-xs text-stone-500 dark:text-stone-400 mb-3 font-medium uppercase tracking-wide">Browse by Category</p>
                      <div className="space-y-1">
                        {articleCategories.map((category, idx) => {
                          const IconComponent = category.icon;
                          const count = categoryCounts[category.name] || 0;
                          return (
                            <a
                              key={idx}
                              href={`#${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                              className="flex items-center justify-between p-3 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors group"
                              data-testid={`articles-menu-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                              onClick={() => {
                                const section = document.getElementById(category.name.toLowerCase().replace(/\s+/g, '-'));
                                if (section) section.scrollIntoView({ behavior: 'smooth' });
                              }}
                            >
                              <div className="flex items-center space-x-3">
                                <IconComponent className={`w-5 h-5 ${category.color}`} />
                                <span className="text-sm font-medium text-stone-700 dark:text-stone-200 group-hover:text-emerald-700 dark:group-hover:text-emerald-400">{category.name}</span>
                              </div>
                              <span className="text-xs text-stone-400 bg-stone-100 dark:bg-stone-700 px-2 py-1 rounded-full">{count}</span>
                            </a>
                          );
                        })}
                      </div>
                      <div className="mt-4 pt-4 border-t border-stone-200 dark:border-stone-700">
                        <a 
                          href="https://www.gardenandgreenhouse.net/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center justify-center text-sm text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 font-medium"
                        >
                          Visit gardenandgreenhouse.net <ChevronRight className="w-4 h-4 ml-1" />
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Garden Hub - Main Affiliate Directory */}
              <Link to="/garden-hub" className="nav-link">Garden Hub</Link>
              
              {/* Digital Magazine Link */}
              <a 
                href="https://newsstand.joomag.com/en/garden-greenhouse/M0984536001481748758" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="nav-link flex items-center"
                data-testid="digital-magazine-link"
              >
                <BookOpen className="w-4 h-4 mr-1" />
                Digital Magazine
              </a>
              
              <a href="#ai-assistant" className="nav-link">AI Assistant</a>
              <div className="flex items-center space-x-3">
                <button className="p-2 hover:bg-stone-100 dark:hover:bg-stone-700 rounded-lg transition-colors" data-testid="search-btn">
                  <Search className="w-5 h-5 text-stone-600 dark:text-stone-300" />
                </button>
                {/* Dark Mode Toggle */}
                <button 
                  onClick={() => setDarkMode(!darkMode)}
                  className="p-2 hover:bg-stone-100 dark:hover:bg-stone-700 rounded-lg transition-colors"
                  data-testid="dark-mode-toggle"
                  title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  {darkMode ? (
                    <Sun className="w-5 h-5 text-amber-500" />
                  ) : (
                    <Moon className="w-5 h-5 text-stone-600" />
                  )}
                </button>
                <Link to="/garden-hub">
                  <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="nav-shop-btn">
                    Shop Now
                  </Button>
                </Link>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-stone-200">
            <div className="px-4 py-4 space-y-3">
              <Link to="/garden-hub" className="block py-2 text-stone-700 hover:text-emerald-700 font-semibold">
                Garden Hub
              </Link>
              <a 
                href="https://newsstand.joomag.com/en/garden-greenhouse/M0984536001481748758" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block py-2 text-stone-700 hover:text-emerald-700"
              >
                📖 Digital Magazine
              </a>
              <a href="#shop" className="block py-2 text-stone-700 hover:text-emerald-700">Shop</a>
              <div className="py-2">
                <p className="text-stone-700 font-semibold mb-2">Articles</p>
                <div className="pl-4 space-y-2">
                  {articleCategories.map((category, idx) => (
                    <a 
                      key={idx}
                      href={`#${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className="block py-1 text-sm text-stone-600 hover:text-emerald-700"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {category.name} ({categoryCounts[category.name] || 0})
                    </a>
                  ))}
                </div>
              </div>
              <a href="#ai-assistant" className="block py-2 text-stone-700 hover:text-emerald-700">AI Assistant</a>
            </div>
          </div>
        )}
      </nav>
      </div> {/* End Sticky Header Container */}

      {/* Hero Section - Full Width Article Carousel */}
      <section className="relative">
        {/* Full Width Carousel */}
        <div className="relative h-[70vh] min-h-[500px] max-h-[700px] overflow-hidden">
          {/* Carousel Images */}
          {featuredArticles.length > 0 ? featuredArticles.map((article, idx) => (
            <a
              key={idx}
              href={article.url || `https://www.gardenandgreenhouse.net/`}
              target="_blank"
              rel="noopener noreferrer"
              className={`absolute inset-0 transition-opacity duration-700 ${
                idx === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
              data-testid={`carousel-slide-${idx}`}
            >
              <img
                src={getArticleImage(article)}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              {/* Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20"></div>
              
              {/* Article Info Overlay - Centered */}
              <div className="absolute inset-0 flex items-end justify-center pb-16 sm:pb-24">
                <div className="text-center text-white max-w-4xl px-4 sm:px-8">
                  <span className="inline-block bg-emerald-600 text-white text-xs sm:text-sm font-semibold px-3 sm:px-4 py-1 sm:py-1.5 rounded-full mb-2 sm:mb-4">
                    {article.category}
                  </span>
                  <h2 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4 leading-tight line-clamp-2">
                    {article.title}
                  </h2>
                  <p className="text-stone-200 text-sm sm:text-lg mb-4 sm:mb-6 max-w-2xl mx-auto line-clamp-2 hidden sm:block">
                    {article.excerpt}
                  </p>
                  <div className="inline-flex items-center text-sm sm:text-lg font-semibold bg-white/20 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-full hover:bg-white/30 transition-colors">
                    <span className="mr-2">Read Article</span>
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                </div>
              </div>
            </a>
          )) : (
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-700 to-emerald-900 flex items-center justify-center">
              <div className="text-center text-white p-8">
                <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-xl">Loading articles...</p>
              </div>
            </div>
          )}

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-14 sm:h-14 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
            data-testid="carousel-prev"
            aria-label="Previous slide"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 rotate-180 text-stone-900" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-14 sm:h-14 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
            data-testid="carousel-next"
            aria-label="Next slide"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-stone-900" />
          </button>

          {/* Carousel Dots */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-2">
            {featuredArticles.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`h-2 rounded-full transition-all ${
                  idx === currentSlide ? 'bg-white w-10' : 'bg-white/50 w-2'
                }`}
                data-testid={`carousel-dot-${idx}`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>

          {/* Article Counter Badge - Top Left - Hidden on small mobile */}
          <div className="absolute top-4 left-4 sm:top-6 sm:left-6 bg-white/95 backdrop-blur-sm rounded-lg sm:rounded-xl px-3 py-2 sm:px-4 sm:py-3 shadow-lg z-20 hidden sm:block">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-lg sm:text-xl font-bold text-stone-900">{articles.length}+</p>
                <p className="text-xs text-stone-600">Articles</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Original Content - gardenandgreenhouse.net articles */}
      <section className="py-16 bg-gradient-to-b from-emerald-50 to-white" id="featured">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                <span className="text-sm font-semibold text-amber-600 uppercase tracking-wide">Original Content</span>
              </div>
              <h2 className="section-title">From GardenandGreenhouse.net</h2>
              <p className="text-stone-600 mt-2">Premium articles from our flagship publication</p>
            </div>
            <a href="https://www.gardenandgreenhouse.net/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:text-emerald-800 flex items-center">
              Visit .NET Site <ChevronRight className="w-4 h-4 ml-1" />
            </a>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {articles.filter(a => a.source === 'gardenandgreenhouse.net').slice(0, 8).map((article, idx) => (
              <a key={article.id || idx} href={article.url} target="_blank" rel="noopener noreferrer" className="group">
                <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-emerald-200">
                  <div className="relative h-40 overflow-hidden">
                    <img src={getArticleImage(article)} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <div className="absolute top-2 right-2">
                      <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">FEATURED</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-emerald-600 font-semibold">{article.category}</span>
                    <h3 className="font-semibold text-stone-900 mt-1 line-clamp-2 group-hover:text-emerald-700 transition-colors">{article.title}</h3>
                  </div>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Articles */}
      <section className="py-16 bg-white" id="articles">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <h2 className="section-title">More from Partner Sources</h2>
              <button 
                onClick={handleSyncArticles}
                disabled={syncing}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all ${
                  syncing 
                    ? 'border-emerald-400 bg-emerald-50 text-emerald-600' 
                    : 'border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white'
                }`}
                data-testid="refresh-articles-btn"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                <span className="text-sm font-semibold">{syncing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
            <a href="https://www.gardenandgreenhouse.net/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:text-emerald-800 flex items-center" data-testid="view-all-articles">
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </a>
          </div>
          <p className="text-stone-600 mb-6">Expert articles and growing guides ({articles.length} articles)</p>

          <div className="grid md:grid-cols-3 gap-6">
            {(articles.slice(0, 3).length > 0 ? articles.slice(0, 3) : []).map((article, idx) => (
              <Card key={article.id || idx} className="article-card-epic" data-testid={`trending-article-${idx}`}>
                <div className="relative h-48 overflow-hidden rounded-t-lg">
                  <img src={getArticleImage(article)} alt={article.title} className="w-full h-full object-cover hover-scale" />
                  <div className="absolute top-3 left-3">
                    <span className="category-badge">{article.category}</span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="article-card-title">{article.title}</h3>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-stone-500 capitalize">{article.source?.replace('.com', '').replace('.net', '') || 'Garden & Greenhouse'}</span>
                    <a href={article.url || '#'} target="_blank" rel="noopener noreferrer" className="read-more-link" data-testid={`read-article-${idx}`}>
                      Read More <ChevronRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </Card>
            ))}
            {loading && (
              <div className="col-span-3 text-center py-8 text-stone-500">Loading articles...</div>
            )}
          </div>
        </div>
      </section>

      {/* Digital Magazine Section */}
      <section className="py-16 bg-gradient-to-br from-emerald-900 to-emerald-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="inline-block bg-emerald-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                📖 EST. 1998
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Garden & Greenhouse Digital Magazine
              </h2>
              <p className="text-emerald-100 text-lg mb-6">
                Access our complete library of published magazines featuring expert articles, seasonal guides, product reviews, and exclusive gardening tips from industry professionals.
              </p>
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-5 h-5 text-emerald-300" />
                  <span className="text-emerald-100">100+ Issues</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Award className="w-5 h-5 text-emerald-300" />
                  <span className="text-emerald-100">25+ Years</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-emerald-300" />
                  <span className="text-emerald-100">50K+ Readers</span>
                </div>
              </div>
              <a 
                href="https://newsstand.joomag.com/en/garden-greenhouse/M0984536001481748758" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center bg-white text-emerald-800 font-semibold px-6 py-3 rounded-lg hover:bg-emerald-50 transition-colors"
                data-testid="browse-magazine-btn"
              >
                <BookOpen className="w-5 h-5 mr-2" />
                Browse Digital Issues
                <ChevronRight className="w-5 h-5 ml-2" />
              </a>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="bg-white rounded-lg shadow-xl overflow-hidden transform hover:scale-105 transition-transform">
                    <img src="https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=300&q=80" alt="Magazine Cover - Greenhouse Edition" className="w-full h-40 object-cover" />
                    <div className="p-3">
                      <p className="text-xs text-emerald-700 font-semibold">WINTER 2026</p>
                      <p className="text-sm font-bold text-stone-900">Greenhouse Special</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow-xl overflow-hidden transform hover:scale-105 transition-transform">
                    <img src="https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=300&q=80" alt="Magazine Cover - Seeds Edition" className="w-full h-40 object-cover" />
                    <div className="p-3">
                      <p className="text-xs text-emerald-700 font-semibold">SPRING 2026</p>
                      <p className="text-sm font-bold text-stone-900">Seed Starting Guide</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4 mt-8">
                  <div className="bg-white rounded-lg shadow-xl overflow-hidden transform hover:scale-105 transition-transform">
                    <img src="https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=300&q=80" alt="Magazine Cover - Landscaping Edition" className="w-full h-40 object-cover" />
                    <div className="p-3">
                      <p className="text-xs text-emerald-700 font-semibold">SUMMER 2026</p>
                      <p className="text-sm font-bold text-stone-900">Landscaping Ideas</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow-xl overflow-hidden transform hover:scale-105 transition-transform">
                    <img src="https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=300&q=80" alt="Magazine Cover - Pest Control" className="w-full h-40 object-cover" />
                    <div className="p-3">
                      <p className="text-xs text-emerald-700 font-semibold">FALL 2026</p>
                      <p className="text-sm font-bold text-stone-900">Pest Prevention</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shop Section */}
      <section className="py-16 bg-stone-50" id="shop">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">Shop Garden & Greenhouse</h2>
            <div className="flex flex-wrap justify-center gap-8 mt-8">
              <div className="flex flex-col items-center">
                <Award className="w-8 h-8 text-emerald-700 mb-2" />
                <span className="text-sm font-semibold text-stone-900">Unbeatable Quality</span>
              </div>
              <div className="flex flex-col items-center">
                <Users className="w-8 h-8 text-emerald-700 mb-2" />
                <span className="text-sm font-semibold text-stone-900">Expert Support</span>
              </div>
              <div className="flex flex-col items-center">
                <Package className="w-8 h-8 text-emerald-700 mb-2" />
                <span className="text-sm font-semibold text-stone-900">Fast Shipping</span>
              </div>
            </div>
          </div>

          {/* Featured Product */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-12">
            <div className="grid md:grid-cols-2 gap-8">
              <img
                src="https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e?w=700&q=80"
                alt="Raised garden bed"
                className="w-full h-full object-cover"
              />
              <div className="p-8 flex flex-col justify-center">
                <span className="text-emerald-700 font-semibold text-sm mb-2">FEATURED PRODUCT</span>
                <h3 className="text-3xl font-bold text-stone-900 mb-4">3-Minute Raised Garden Bed</h3>
                <p className="text-stone-600 mb-6">
                  Your fast track to a premium garden experience! This raised garden bed assembles in minutes, tool-free. Perfect for beginners and experienced gardeners.
                </p>
                <div className="flex items-baseline space-x-2 mb-6">
                  <span className="text-4xl font-bold text-emerald-700">$129.99</span>
                  <span className="text-stone-500 line-through">$149.99</span>
                </div>
                <Button className="cta-button-primary w-fit" data-testid="buy-featured-product">
                  Buy Now
                </Button>
              </div>
            </div>
          </div>

          {/* Product Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Organic Seed Collection', price: '$52.49', image: 'https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?w=400&q=80', rating: 4.9, inStock: true },
              { name: 'Premium Garden Tools', price: '$89.99', image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&q=80', rating: 4.8, inStock: true },
              { name: 'Soil & Amendments Kit', price: '$34.99', image: 'https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=400&q=80', rating: 4.7, inStock: true },
              { name: 'Smart Drip System', price: '$149.99', image: 'https://images.unsplash.com/photo-1558293842-c0fd3db86157?w=400&q=80', rating: 4.6, inStock: false },
            ].map((product, idx) => (
              <Card key={idx} className="product-card-epic" data-testid={`product-${idx}`}>
                <div className="relative h-56 overflow-hidden rounded-t-lg">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover hover-scale" />
                  {!product.inStock && (
                    <div className="absolute top-3 right-3 bg-stone-900 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Out of Stock
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center space-x-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'fill-amber-400 text-amber-400' : 'text-stone-300'}`} />
                    ))}
                    <span className="text-sm text-stone-600 ml-2">({product.rating})</span>
                  </div>
                  <h3 className="font-semibold text-stone-900 mb-2">{product.name}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-emerald-700">{product.price}</span>
                    <Button 
                      size="sm" 
                      className="bg-emerald-700 hover:bg-emerald-800 text-white"
                      disabled={!product.inStock}
                      data-testid={`buy-product-${idx}`}
                    >
                      {product.inStock ? 'Buy Now' : 'Notify Me'}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button variant="outline" className="border-emerald-700 text-emerald-700 hover:bg-emerald-50" data-testid="shop-all-btn">
              Shop All Products
            </Button>
          </div>
        </div>
      </section>

      {/* AI Assistant Section */}
      <section className="py-16 bg-white" id="ai-assistant">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="ai-badge">✨ AI-Powered Garden Expert</span>
              </div>
              <h2 className="section-title">Get Instant Plant Care Advice</h2>
              <p className="text-lg text-stone-600 leading-relaxed">
                Our AI assistant has been trained on thousands of gardening articles from gardenandgreenhouse.net and expert knowledge. Get personalized recommendations for your specific garden conditions, climate, and plant needs.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-emerald-700 font-bold text-sm">✓</span>
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900">Plant Identification & Care</p>
                    <p className="text-stone-600">Describe your plants for instant ID and care instructions</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-emerald-700 font-bold text-sm">✓</span>
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900">Personalized Recommendations</p>
                    <p className="text-stone-600">Get tips tailored to your garden&apos;s specific needs</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-emerald-700 font-bold text-sm">✓</span>
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900">1 Free Question</p>
                    <p className="text-stone-600">Try our AI assistant with one complimentary question</p>
                  </div>
                </div>
              </div>

              {freeSearchUsed && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-800 text-sm">
                    <strong>Free question used!</strong> Contact us for premium access to unlimited AI gardening assistance.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl shadow-xl p-6 border border-emerald-100">
              <div className="flex items-center justify-between pb-4 border-b border-stone-200">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-emerald-700 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-stone-900">Garden AI Assistant</p>
                    <p className="text-sm text-emerald-700 flex items-center">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
                      Online Now
                    </p>
                  </div>
                </div>
                {!freeSearchUsed && (
                  <span className="bg-emerald-100 text-emerald-800 text-xs font-semibold px-2 py-1 rounded-full">
                    1 Free Question
                  </span>
                )}
              </div>
              <div className="space-y-4 mt-6 h-80 overflow-y-auto" id="chat-messages">
                {chatMessages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`${msg.role === 'user' 
                      ? 'bg-emerald-700 rounded-2xl rounded-tr-sm text-white' 
                      : 'bg-stone-100 rounded-2xl rounded-tl-sm text-stone-800'} px-4 py-3 max-w-sm`}>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-stone-100 rounded-2xl rounded-tl-sm px-4 py-3">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-6 pt-4 border-t border-stone-200">
                <div className="flex items-center space-x-2">
                  <Input 
                    placeholder={freeSearchUsed ? "Free question used" : "Ask about your garden..."} 
                    className="flex-1" 
                    data-testid="ai-input-field"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                    disabled={freeSearchUsed || chatLoading}
                  />
                  <Button 
                    className="bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50" 
                    data-testid="send-ai-message"
                    onClick={sendChatMessage}
                    disabled={freeSearchUsed || chatLoading || !chatInput.trim()}
                  >
                    {chatLoading ? '...' : 'Send'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Growing Guides Categories */}
      <section className="py-16 bg-stone-50" id="guides">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">Shop by Category</h2>
            <p className="text-stone-600 mt-2">Find exactly what you need for your garden</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              { name: 'Metal Raised Beds', image: 'https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e?w=300&q=80' },
              { name: 'Seeds & Starting', image: 'https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?w=300&q=80' },
              { name: 'Soil & Amendments', image: 'https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=300&q=80' },
              { name: 'Gardening Tools', image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=300&q=80' },
              { name: 'Watering Systems', image: 'https://images.unsplash.com/photo-1558293842-c0fd3db86157?w=300&q=80' },
            ].map((category, idx) => (
              <Card key={idx} className="category-card" data-testid={`category-${idx}`}>
                <div className="relative h-40 overflow-hidden rounded-lg mb-3">
                  <img src={category.image} alt={category.name} className="w-full h-full object-cover hover-scale" />
                </div>
                <h3 className="text-center font-semibold text-stone-900">{category.name}</h3>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections (Seeds, Vegetables) */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Greenhouses Section */}
          <div className="mb-16" id="greenhouses">
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title">Greenhouses</h2>
              <a href="https://www.gardenandgreenhouse.net/category/indoor-gardening/greenhouses/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:text-emerald-800 flex items-center" data-testid="view-all-greenhouses">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </div>
            <p className="text-stone-600 mb-8">Expert greenhouse tips and maintenance guides from gardenandgreenhouse.net</p>
            
            <div className="grid md:grid-cols-4 gap-6">
              {getArticlesByCategory('Greenhouses').map((article, idx) => (
                <Card key={article.id || idx} className="guide-card" data-testid={`greenhouse-guide-${idx}`}>
                  <img src={getArticleImage(article)} alt={article.title} className="w-full h-40 object-cover rounded-t-lg" />
                  <div className="p-4">
                    <span className="category-badge mb-2">Greenhouses</span>
                    <h3 className="font-semibold text-stone-900 mt-2 text-sm">{article.title}</h3>
                    <a href={article.url || '#'} target="_blank" rel="noopener noreferrer" className="read-more-link text-xs mt-3 inline-flex" data-testid={`read-greenhouse-${idx}`}>
                      Read <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* General Gardening Section */}
          <div className="mb-16" id="general-gardening">
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title">General Gardening</h2>
              <a href="https://www.gardenandgreenhouse.net/category/general/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:text-emerald-800 flex items-center" data-testid="view-all-general">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </div>
            <p className="text-stone-600 mb-8">Essential tips and tricks for every gardener from gardenandgreenhouse.net</p>
            
            <div className="grid md:grid-cols-4 gap-6">
              {getArticlesByCategory('General Gardening').map((article, idx) => (
                <Card key={article.id || idx} className="guide-card" data-testid={`general-guide-${idx}`}>
                  <img src={getArticleImage(article)} alt={article.title} className="w-full h-40 object-cover rounded-t-lg" />
                  <div className="p-4">
                    <span className="category-badge mb-2">General Gardening</span>
                    <h3 className="font-semibold text-stone-900 mt-2 text-sm">{article.title}</h3>
                    <a href={article.url || '#'} target="_blank" rel="noopener noreferrer" className="read-more-link text-xs mt-3 inline-flex" data-testid={`read-general-${idx}`}>
                      Read <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Pest Control Section */}
          <div className="mb-16" id="pest-control">
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title">Pest Control</h2>
              <a href="https://www.gardenandgreenhouse.net/category/pest-control/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:text-emerald-800 flex items-center" data-testid="view-all-pest">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </div>
            <p className="text-stone-600 mb-8">Keep your garden healthy and pest-free with expert advice from gardenandgreenhouse.net</p>
            
            <div className="grid md:grid-cols-4 gap-6">
              {getArticlesByCategory('Pest Control').map((article, idx) => (
                <Card key={article.id || idx} className="guide-card" data-testid={`pest-guide-${idx}`}>
                  <img src={getArticleImage(article)} alt={article.title} className="w-full h-40 object-cover rounded-t-lg" />
                  <div className="p-4">
                    <span className="category-badge mb-2">Pest Control</span>
                    <h3 className="font-semibold text-stone-900 mt-2 text-sm">{article.title}</h3>
                    <a href={article.url || '#'} target="_blank" rel="noopener noreferrer" className="read-more-link text-xs mt-3 inline-flex" data-testid={`read-pest-${idx}`}>
                      Read <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Growing Tips Section */}
          <div className="mb-16" id="growing-tips">
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title">Growing Tips</h2>
              <a href="https://www.gardenandgreenhouse.net/category/growing-tips/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:text-emerald-800 flex items-center" data-testid="view-all-tips">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </div>
            <p className="text-stone-600 mb-8">Master gardening techniques with proven tips from gardenandgreenhouse.net</p>
            
            <div className="grid md:grid-cols-4 gap-6">
              {getArticlesByCategory('Growing Tips').map((article, idx) => (
                <Card key={article.id || idx} className="guide-card" data-testid={`tips-guide-${idx}`}>
                  <img src={getArticleImage(article)} alt={article.title} className="w-full h-40 object-cover rounded-t-lg" />
                  <div className="p-4">
                    <span className="category-badge mb-2">Growing Tips</span>
                    <h3 className="font-semibold text-stone-900 mt-2 text-sm">{article.title}</h3>
                    <a href={article.url || '#'} target="_blank" rel="noopener noreferrer" className="read-more-link text-xs mt-3 inline-flex" data-testid={`read-tips-${idx}`}>
                      Read <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Indoor Gardening Section */}
          <div className="mb-16" id="indoor-gardening">
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title">Indoor Gardening</h2>
              <a href="https://www.gardenandgreenhouse.net/category/indoor-gardening/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:text-emerald-800 flex items-center" data-testid="view-all-indoor">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </div>
            <p className="text-stone-600 mb-8">Grow thriving plants indoors with guides from gardenandgreenhouse.net</p>
            
            <div className="grid md:grid-cols-4 gap-6">
              {getArticlesByCategory('Indoor Gardening').map((article, idx) => (
                <Card key={article.id || idx} className="guide-card" data-testid={`indoor-guide-${idx}`}>
                  <img src={getArticleImage(article)} alt={article.title} className="w-full h-40 object-cover rounded-t-lg" />
                  <div className="p-4">
                    <span className="category-badge mb-2">Indoor Gardening</span>
                    <h3 className="font-semibold text-stone-900 mt-2 text-sm">{article.title}</h3>
                    <a href={article.url || '#'} target="_blank" rel="noopener noreferrer" className="read-more-link text-xs mt-3 inline-flex" data-testid={`read-indoor-${idx}`}>
                      Read <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Landscaping Section */}
          <div className="mb-16" id="landscaping">
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title">Landscaping</h2>
              <a href="https://www.gardenandgreenhouse.net/category/landscaping/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:text-emerald-800 flex items-center" data-testid="view-all-landscaping">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </div>
            <p className="text-stone-600 mb-8">Transform your outdoor spaces with professional landscaping tips from gardenandgreenhouse.net</p>
            
            <div className="grid md:grid-cols-4 gap-6">
              {getArticlesByCategory('Landscaping').map((article, idx) => (
                <Card key={article.id || idx} className="guide-card" data-testid={`landscaping-guide-${idx}`}>
                  <img src={getArticleImage(article)} alt={article.title} className="w-full h-40 object-cover rounded-t-lg" />
                  <div className="p-4">
                    <span className="category-badge mb-2">Landscaping</span>
                    <h3 className="font-semibold text-stone-900 mt-2 text-sm">{article.title}</h3>
                    <a href={article.url || '#'} target="_blank" rel="noopener noreferrer" className="read-more-link text-xs mt-3 inline-flex" data-testid={`read-landscaping-${idx}`}>
                      Read <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Seeds Section */}
          <div className="mb-12" id="seeds">
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title">Seeds</h2>
              <a href="https://www.gardenandgreenhouse.net/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:text-emerald-800 flex items-center" data-testid="view-all-seeds">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </div>
            <p className="text-stone-600 mb-8">Looking to get your seeds started indoors or direct sown? Find comprehensive guides from gardenandgreenhouse.net</p>
            
            <div className="grid md:grid-cols-4 gap-6">
              {getArticlesByCategory('Seeds').map((article, idx) => (
                <Card key={article.id || idx} className="guide-card" data-testid={`seed-guide-${idx}`}>
                  <img src={getArticleImage(article)} alt={article.title} className="w-full h-40 object-cover rounded-t-lg" />
                  <div className="p-4">
                    <span className="category-badge mb-2">Seeds</span>
                    <h3 className="font-semibold text-stone-900 mt-2">{article.title}</h3>
                    <a href={article.url || '#'} target="_blank" rel="noopener noreferrer" className="read-more-link text-xs mt-3 inline-flex" data-testid={`read-seed-${idx}`}>
                      Read <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title">Garden & Home</h2>
              <a href="https://www.gardenandgreenhouse.net/category/general/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:text-emerald-800 flex items-center" data-testid="view-all-vegetables">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </div>
            <p className="text-stone-600 mb-8">Beautiful gardens and outdoor living spaces from gardenandgreenhouse.net</p>
            
            <div className="grid md:grid-cols-4 gap-6">
              {/* Show additional General Gardening articles not already shown */}
              {articles.filter(a => a.category === 'General Gardening').slice(4, 8).map((article, idx) => (
                <Card key={article.id || idx} className="guide-card" data-testid={`veg-guide-${idx}`}>
                  <img src={getArticleImage(article)} alt={article.title} className="w-full h-40 object-cover rounded-t-lg" />
                  <div className="p-4">
                    <span className="category-badge mb-2">General Gardening</span>
                    <h3 className="font-semibold text-stone-900 mt-2">{article.title}</h3>
                    <a href={article.url || '#'} target="_blank" rel="noopener noreferrer" className="read-more-link text-xs mt-3 inline-flex" data-testid={`read-veg-${idx}`}>
                      Read <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eco-gardens-2/artifacts/8awup57n_GardenAndGreenhouse%20FINAL%20LOGO.png" 
                  alt="Garden & Greenhouse Logo - Est. 2018" 
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h3 className="text-white font-bold text-xl">Garden & Greenhouse</h3>
                  <p className="text-emerald-400 text-xs font-semibold">SIMPLE, PRACTICAL GARDENING TIPS</p>
                </div>
              </div>
              <p className="text-sm text-stone-400">Your trusted source for gardening expertise and premium supplies.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Shop</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-emerald-400">Raised Beds</a></li>
                <li><a href="#" className="hover:text-emerald-400">Seeds</a></li>
                <li><a href="#" className="hover:text-emerald-400">Tools</a></li>
                <li><a href="#" className="hover:text-emerald-400">Soil & Amendments</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Learn</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://www.gardenandgreenhouse.net/" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400">Articles (.NET)</a></li>
                <li><a href="#" className="hover:text-emerald-400">Growing Guides</a></li>
                <li><a href="#" className="hover:text-emerald-400">AI Assistant</a></li>
                <li><a href="#" className="hover:text-emerald-400">Garden Quiz</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/write-for-us" className="hover:text-emerald-400">Write for Us</a></li>
                <li><a href="/contact" className="hover:text-emerald-400">Contact</a></li>
                <li><a href="/list-your-business" className="hover:text-emerald-400">List Your Business</a></li>
                <li><a href="/affiliate-disclosure" className="hover:text-emerald-400">Affiliate Disclosure</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-800 mt-8 pt-8">
            <div className="text-center text-sm text-stone-400 space-y-2">
              <p>© 2026 Garden & Greenhouse. All rights reserved.</p>
              <p className="text-xs max-w-4xl mx-auto">
                <strong>Affiliate Disclosure:</strong> Garden & Greenhouse participates in various affiliate marketing programs. 
                We may earn commissions from qualifying purchases made through links on our site at no additional cost to you.
              </p>
              <p className="text-xs max-w-4xl mx-auto">
                <strong>AI Content:</strong> Some content on this site is generated using artificial intelligence technology. 
                While we strive for accuracy, always consult with professionals for specific gardening advice.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                <a href="/terms" className="text-xs hover:text-emerald-400 transition-colors">Terms of Service</a>
                <a href="/privacy" className="text-xs hover:text-emerald-400 transition-colors">Privacy Policy</a>
                <a href="/affiliate-disclosure" className="text-xs hover:text-emerald-400 transition-colors">Affiliate Disclosure</a>
                <a href="/contact" className="text-xs hover:text-emerald-400 transition-colors">Contact Us</a>
                <a href="/write-for-us" className="text-xs hover:text-emerald-400 transition-colors">Write for Us</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/garden-hub" element={<GardenHub />} />
            <Route path="/list-your-business" element={<ListYourBusiness />} />
            <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/write-for-us" element={<WriteForUs />} />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
